import { createContext, useContext, ReactNode, useMemo, useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Database } from "@/integrations/supabase/types";

type Child = Database["public"]["Tables"]["children"]["Row"];
type Profile = Database["public"]["Tables"]["profiles"]["Row"];

interface AppDataContextType {
  // Children data
  children: Child[];
  childrenLoading: boolean;
  refetchChildren: () => void;

  // Approved profiles (parents)
  approvedProfiles: Profile[];
  profilesLoading: boolean;
  refetchProfiles: () => void;

  // Parent-children mappings
  parentChildrenMap: Map<string, string[]>;
  childParentsMap: Map<string, string[]>;
  parentChildrenLoading: boolean;

  // Internal: subscriber counters used to opt-in to fetches
  _subscribe: (key: "children" | "profiles" | "parentChildren") => void;
  _unsubscribe: (key: "children" | "profiles" | "parentChildren") => void;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

const STALE_TIME = 1000 * 60 * 5; // 5 minutes

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { user, isStaff } = useAuth();

  // Subscriber counters — queries only run when at least one consumer subscribes.
  const [subs, setSubs] = useState({ children: 0, profiles: 0, parentChildren: 0 });

  const _subscribe = useCallback((key: "children" | "profiles" | "parentChildren") => {
    setSubs((prev) => ({ ...prev, [key]: prev[key] + 1 }));
  }, []);
  const _unsubscribe = useCallback((key: "children" | "profiles" | "parentChildren") => {
    setSubs((prev) => ({ ...prev, [key]: Math.max(0, prev[key] - 1) }));
  }, []);

  // Fetch all children (staff only, opt-in)
  const {
    data: allChildren = [],
    isLoading: childrenLoading,
    refetch: refetchChildren,
  } = useQuery({
    queryKey: ["app-children"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .order("full_name");
      if (error) throw error;
      return data || [];
    },
    staleTime: STALE_TIME,
    enabled: !!user && isStaff && subs.children > 0,
  });

  // Fetch approved profiles (opt-in)
  const {
    data: approvedProfiles = [],
    isLoading: profilesLoading,
    refetch: refetchProfiles,
  } = useQuery({
    queryKey: ["app-profiles-approved"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("status", "approved")
        .order("full_name");
      if (error) throw error;
      return data || [];
    },
    staleTime: STALE_TIME,
    enabled: !!user && isStaff && subs.profiles > 0,
  });

  // Fetch parent-children mappings (opt-in)
  const {
    data: parentChildrenData = [],
    isLoading: parentChildrenLoading,
  } = useQuery({
    queryKey: ["app-parent-children"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("parent_children")
        .select("parent_id, child_id");
      if (error) throw error;
      return data || [];
    },
    staleTime: STALE_TIME,
    enabled: !!user && isStaff && subs.parentChildren > 0,
  });

  // Create maps for quick lookups
  const { parentChildrenMap, childParentsMap } = useMemo(() => {
    const pMap = new Map<string, string[]>();
    const cMap = new Map<string, string[]>();

    parentChildrenData.forEach(({ parent_id, child_id }) => {
      if (!pMap.has(parent_id)) pMap.set(parent_id, []);
      pMap.get(parent_id)!.push(child_id);

      if (!cMap.has(child_id)) cMap.set(child_id, []);
      cMap.get(child_id)!.push(parent_id);
    });

    return { parentChildrenMap: pMap, childParentsMap: cMap };
  }, [parentChildrenData]);

  const value: AppDataContextType = {
    children: allChildren,
    childrenLoading,
    refetchChildren,
    approvedProfiles,
    profilesLoading,
    refetchProfiles,
    parentChildrenMap,
    childParentsMap,
    parentChildrenLoading,
    _subscribe,
    _unsubscribe,
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
}

/**
 * Subscribes the calling component to one or more AppData slices.
 * Only when at least one component subscribes does the underlying query run.
 *
 * Example:
 *   const { children } = useAppData(["children"]);
 */
export function useAppData(slices: Array<"children" | "profiles" | "parentChildren"> = ["children", "profiles", "parentChildren"]) {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error("useAppData must be used within an AppDataProvider");
  }

  useEffect(() => {
    slices.forEach((s) => context._subscribe(s));
    return () => {
      slices.forEach((s) => context._unsubscribe(s));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slices.join(",")]);

  return context;
}

// Optional hook that returns undefined if outside provider (for gradual migration)
export function useAppDataOptional() {
  return useContext(AppDataContext);
}
